import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Swords, Calendar, Library, ArrowLeft, Users } from 'lucide-react';

type GameMode = 'training' | 'challenge' | 'daily' | 'category' | 'multiplayer';

interface ModeCardData {
  id: GameMode;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  comingSoon?: boolean;
}

const ModeSelectContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xl};
  padding-top: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  max-width: 800px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 767px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const ModesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  max-width: 1200px;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const ModeCard = styled(motion.div)<{ $color: string; $disabled?: boolean }>`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  border: 3px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: ${({ $color }) => $color};
  }

  &:hover {
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'translateY(-8px)')};
    border-color: ${({ $color, $disabled }) => ($disabled ? 'transparent' : $color)};
    box-shadow: ${({ theme, $disabled }) =>
      $disabled ? theme.shadows.lg : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
  }
`;

const ModeIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  svg {
    width: 48px;
    height: 48px;
    stroke-width: 1.5;
  }
`;

const ModeTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  text-align: center;
`;

const ModeDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  text-align: center;
  margin: 0;
`;

const ComingSoonBadge = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ModeSelectScreen: React.FC = () => {
  const navigate = useNavigate();

  const modes: ModeCardData[] = [
    {
      id: 'training',
      title: 'Training Mode',
      description: 'Practice at your own pace. No timer, detailed explanations, and skip options. Perfect for learning!',
      icon: <BookOpen />,
      color: '#4CAF50',
    },
    {
      id: 'challenge',
      title: 'Challenge Mode',
      description: 'Test your knowledge! Timed questions, streak bonuses, and compete for high scores.',
      icon: <Swords />,
      color: '#FF9800',
    },
    {
      id: 'multiplayer',
      title: 'Multiplayer Battle',
      description: 'Challenge other players in real-time! Compete head-to-head to prove your Bible knowledge.',
      icon: <Users />,
      color: '#E91E63',
    },
    {
      id: 'daily',
      title: 'Daily Quiz',
      description: 'New questions every day! Complete your daily challenge and earn bonus XP.',
      icon: <Calendar />,
      color: '#2196F3',
      comingSoon: true,
    },
    {
      id: 'category',
      title: 'Category Practice',
      description: 'Focus on specific books or topics. Master each category one at a time.',
      icon: <Library />,
      color: '#9C27B0',
      comingSoon: true,
    },
  ];

  const handleModeSelect = (mode: GameMode, disabled?: boolean) => {
    if (disabled) return;

    if (mode === 'training') {
      navigate('/game/training');
    } else if (mode === 'challenge') {
      navigate('/game');
    } else if (mode === 'multiplayer') {
      navigate('/game/multiplayer');
    }
  };

  return (
    <ModeSelectContainer>
      <Header>
        <Title>Choose Your Mode</Title>
        <Subtitle>Select a game mode to begin your Bible knowledge journey</Subtitle>
      </Header>

      <ModesGrid>
        {modes.map((mode, index) => (
          <ModeCard
            key={mode.id}
            $color={mode.color}
            $disabled={mode.comingSoon}
            onClick={() => handleModeSelect(mode.id, mode.comingSoon)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            whileHover={!mode.comingSoon ? { scale: 1.02 } : {}}
            whileTap={!mode.comingSoon ? { scale: 0.98 } : {}}
          >
            {mode.comingSoon && <ComingSoonBadge>Coming Soon</ComingSoonBadge>}
            <ModeIcon>{mode.icon}</ModeIcon>
            <ModeTitle>{mode.title}</ModeTitle>
            <ModeDescription>{mode.description}</ModeDescription>
          </ModeCard>
        ))}
      </ModesGrid>

      <BackButton onClick={() => navigate('/')}>
        <ArrowLeft size={20} />
        Back to Home
      </BackButton>
    </ModeSelectContainer>
  );
};

export default ModeSelectScreen;
